import { useState, useEffect } from "react";
import { useAuth, API } from "../../App";
import axios from "axios";
import { toast } from "sonner";
import {
  Users,
  TrendingUp,
  Building2,
  Briefcase,
  MessageSquare,
  Calendar,
  DollarSign,
  Loader2,
  ChevronRight,
  User,
  Clock,
} from "lucide-react";
import { Link } from "react-router-dom";

const TeamDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamStats, setTeamStats] = useState(null);
  const [recentActivities, setRecentActivities] = useState([]);

  useEffect(() => {
    if (user) {
      fetchTeamData();
    }
  }, [user]);

  const fetchTeamData = async () => {
    try {
      // Fetch team members
      const teamRes = await axios.get(`${API}/team/my-team`, { withCredentials: true });
      setTeamMembers(teamRes.data || []);

      // Fetch team statistics based on role
      await fetchTeamStats();
      
      // Fetch recent activities
      await fetchRecentActivities();
    } catch (error) {
      console.error("Failed to fetch team data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamStats = async () => {
    try {
      let stats = {
        totalMembers: 0,
        activeDeals: 0,
        totalLeads: 0,
        pendingInquiries: 0,
        scheduledTours: 0,
        monthlyRevenue: 0,
      };

      // Get CRM stats if available
      const crmRes = await axios.get(`${API}/crm/stats`, { withCredentials: true });
      if (crmRes.data) {
        stats.totalLeads = crmRes.data.leads?.total || 0;
        stats.activeDeals = crmRes.data.deals?.total || 0;
        stats.monthlyRevenue = crmRes.data.deals?.won_value || 0;
      }

      setTeamStats(stats);
    } catch (error) {
      console.error("Failed to fetch team stats:", error);
    }
  };

  const fetchRecentActivities = async () => {
    try {
      const activities = [];

      // Fetch recent leads
      try {
        const leadsRes = await axios.get(`${API}/crm/leads`, { withCredentials: true });
        const recentLeads = (leadsRes.data || []).slice(0, 3).map(lead => ({
          type: "lead",
          title: `New lead: ${lead.name}`,
          time: lead.created_at,
          icon: Users,
        }));
        activities.push(...recentLeads);
      } catch (e) {}

      // Fetch recent deals
      try {
        const dealsRes = await axios.get(`${API}/crm/deals`, { withCredentials: true });
        const recentDeals = (dealsRes.data || []).slice(0, 3).map(deal => ({
          type: "deal",
          title: `Deal: ${deal.title} - K${deal.value?.toLocaleString()}`,
          time: deal.created_at,
          icon: Briefcase,
        }));
        activities.push(...recentDeals);
      } catch (e) {}

      // Sort by time and take top 5
      activities.sort((a, b) => new Date(b.time) - new Date(a.time));
      setRecentActivities(activities.slice(0, 5));
    } catch (error) {
      console.error("Failed to fetch activities:", error);
    }
  };

  const getRoleLabel = (role) => {
    const labels = {
      agent: "Agent",
      data_entry: "Data Entry",
      constructor: "Constructor",
      support: "Support",
    };
    return labels[role] || role;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    
    if (diffHours < 1) return "Just now";
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffHours < 48) return "Yesterday";
    return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="team-dashboard">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Team Dashboard</h1>
        <p className="text-gray-500">
          {user?.role === "letting_manager" && "Overview of your property agents and rental activities"}
          {user?.role === "sales_manager" && "Overview of your sales team and deal pipeline"}
          {user?.role === "accounts" && "Overview of data entry team and financial activities"}
          {user?.role === "project_manager" && "Overview of constructors and project progress"}
          {user?.role === "admin" && "Company-wide team overview"}
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Team Members</p>
              <p className="text-2xl font-bold">{teamMembers.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg border p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <TrendingUp className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Leads</p>
              <p className="text-2xl font-bold">{teamStats?.totalLeads || 0}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg border p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Briefcase className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Active Deals</p>
              <p className="text-2xl font-bold">{teamStats?.activeDeals || 0}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg border p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <DollarSign className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Revenue</p>
              <p className="text-2xl font-bold">K{(teamStats?.monthlyRevenue || 0).toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Team Members */}
        <div className="bg-white rounded-lg border">
          <div className="p-4 border-b flex items-center justify-between">
            <h2 className="font-semibold">Team Members</h2>
            <span className="text-sm text-gray-500">{teamMembers.length} members</span>
          </div>
          <div className="divide-y max-h-80 overflow-y-auto">
            {teamMembers.length > 0 ? (
              teamMembers.map((member) => (
                <div key={member.user_id} className="p-4 flex items-center gap-3 hover:bg-gray-50">
                  {member.picture ? (
                    <img src={member.picture} alt={member.name} className="w-10 h-10 rounded-full" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium">
                      {member.name?.[0]}
                    </div>
                  )}
                  <div className="flex-1">
                    <p className="font-medium">{member.name}</p>
                    <p className="text-sm text-gray-500">{getRoleLabel(member.role)}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </div>
              ))
            ) : (
              <div className="p-8 text-center text-gray-500">
                <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No team members assigned yet</p>
                <p className="text-sm">Team members will appear here based on role hierarchy</p>
              </div>
            )}
          </div>
        </div>

        {/* Recent Activities */}
        <div className="bg-white rounded-lg border">
          <div className="p-4 border-b">
            <h2 className="font-semibold">Recent Activities</h2>
          </div>
          <div className="divide-y max-h-80 overflow-y-auto">
            {recentActivities.length > 0 ? (
              recentActivities.map((activity, idx) => (
                <div key={idx} className="p-4 flex items-center gap-3 hover:bg-gray-50">
                  <div className="p-2 bg-gray-100 rounded-lg">
                    <activity.icon className="w-4 h-4 text-gray-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm">{activity.title}</p>
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDate(activity.time)}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-8 text-center text-gray-500">
                <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No recent activities</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions based on role */}
      <div className="bg-white rounded-lg border p-6">
        <h2 className="font-semibold mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {(user?.role === "sales_manager" || user?.role === "admin") && (
            <>
              <Link to="/dashboard/admin/all-leads" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-center">
                <Users className="w-6 h-6 mx-auto mb-2 text-primary" />
                <p className="text-sm font-medium">View All Leads</p>
              </Link>
              <Link to="/dashboard/admin/all-deals" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-center">
                <Briefcase className="w-6 h-6 mx-auto mb-2 text-primary" />
                <p className="text-sm font-medium">View All Deals</p>
              </Link>
            </>
          )}
          {(user?.role === "letting_manager" || user?.role === "admin") && (
            <>
              <Link to="/dashboard/admin/all-properties" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-center">
                <Building2 className="w-6 h-6 mx-auto mb-2 text-primary" />
                <p className="text-sm font-medium">All Properties</p>
              </Link>
              <Link to="/dashboard/admin/rentals" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-center">
                <DollarSign className="w-6 h-6 mx-auto mb-2 text-primary" />
                <p className="text-sm font-medium">Rental Management</p>
              </Link>
            </>
          )}
          {(user?.role === "accounts" || user?.role === "admin") && (
            <Link to="/dashboard/admin/petty-cash" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-center">
              <DollarSign className="w-6 h-6 mx-auto mb-2 text-primary" />
              <p className="text-sm font-medium">Petty Cash</p>
            </Link>
          )}
          {(user?.role === "project_manager" || user?.role === "admin") && (
            <Link to="/dashboard/admin/projects" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-center">
              <Building2 className="w-6 h-6 mx-auto mb-2 text-primary" />
              <p className="text-sm font-medium">Projects</p>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamDashboard;
